import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Loader2, CheckCircle2 } from 'lucide-react';
import { JobsShell } from '../components/jobs/JobsShell';
import { formatApiError } from '../contexts/JobseekerAuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

export default function JobsForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/jobs/auth/forgot-password`, { email: email.trim().toLowerCase() });
      setDone(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <JobsShell showSigninLink={false}>
      <div className="max-w-md mx-auto px-4 py-14" data-testid="jobs-forgot-password-page">
        <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-8">
          {!done ? (
            <>
              <h1 className="text-2xl font-extrabold text-slate-900">Reset your password</h1>
              <p className="text-slate-600 text-sm mt-1">
                Enter your email and we'll send you a reset link (good for 1 hour).
              </p>

              {error && (
                <div className="mt-4 p-3 bg-rose-50 border border-rose-200 text-rose-700 text-sm rounded-lg" data-testid="forgot-error">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="mt-6 space-y-4">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wide text-slate-700 mb-1.5">Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoFocus
                    className="input-base"
                    data-testid="forgot-email-input"
                  />
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="forgot-submit-btn"
                  className="w-full px-5 py-3 bg-blue-700 hover:bg-blue-800 disabled:opacity-60 text-white font-bold rounded-lg shadow-md flex items-center justify-center gap-2"
                >
                  {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : 'Send reset link'}
                </button>
              </form>
            </>
          ) : (
            <div className="text-center" data-testid="forgot-success">
              <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto" />
              <h1 className="mt-4 text-2xl font-extrabold text-slate-900">Check your inbox</h1>
              <p className="text-slate-600 mt-2 text-sm">
                If an account exists for <span className="font-semibold">{email}</span>, you'll receive a reset link
                shortly. Don't forget to check your spam folder.
              </p>
            </div>
          )}

          <p className="mt-6 text-center text-sm">
            <Link to="/jobs/login" className="text-blue-700 font-bold hover:underline" data-testid="forgot-to-login-link">
              ← Back to sign in
            </Link>
          </p>
        </div>
      </div>
      <style>{`
        .input-base {
          width: 100%; padding: 10px 12px; border: 2px solid #e2e8f0;
          border-radius: 10px; font-size: 15px; background: #fff;
        }
        .input-base:focus { outline: none; border-color: #2563eb;
          box-shadow: 0 0 0 4px rgba(37,99,235,0.12); }
      `}</style>
    </JobsShell>
  );
}
